import React from 'react'
import { Link } from '@uswitch/ev-core/react'
import customValidations from '../../helpers/form-validations'
import { SimpleInput } from '../../elements/simple-input'
import customFunctions from '../../helpers/form-validation-functions'
import SimpleRadio from '../../elements/simple-radio'
import SimpleCheckbox from '../../elements/simple-checkbox'
import SimpleDropDown from '../../elements/simple-drop-down'
import InputAlert from '../../elements/input-alert'
import AddressHistory from '../../compounds/address-history'
import AddressInput from '../../compounds/address-input'
import InputMask from 'react-input-mask'
import SuggestionBox from '../../elements/suggestion-box'
import SLIDER_STYLES from '../../../constants/slider-styles'
import formatter from '../../helpers/form-summary-formatter'
import masks from '../../helpers/form-masks'
import FormatStringToLocale from '../../helpers/number-formatter'
import { Checkbox } from '@uswitch/spark'
import { useComponentTracking } from '@uswitch/ev-utils/component-events'
import { getConsentText } from '@uswitch/rvuconsent'
import { isValidNumber } from '../../helpers/field-input-validation'

const FormStepField = ({
  field,
  register,
  triggerValidation,
  errors,
  setValue,
  getValues,
  formType,
  variant = 'default',
  goToPage,
  updateCompletionState,
  onKeyDownFunction,
  isChild = false,
  trackInteraction = () => {},
  sectionTitle,
  index,
  addressLookup,
}) => {
  const { validation: { custom, customFunction, ...rest } = {} } = field
  let {
    validation,
    name,
    type,
    children,
    placeholder,
    label,
    options = [],
    inputMask: { customMask, mask } = {},
    value = '',
    prefix,
    suffix,
    text,
    links,
    uppercase,
    description,
    inputSize,
    title,
    summaryList,
    suggestionData,
    consentType = null,
    consentName = null,
    fieldVariant,
    subtext,
    minimumRequiredYears,
    privacyPolicyLink,
  } = field

  const trackComponent = useComponentTracking()

  const isSingleRow = fieldVariant === 'singleRow'

  if (custom && customValidations[custom]) {
    validation = {
      ...rest,
      ...customValidations[custom],
    }
  }

  const [consentObj, setConsentObj] = React.useState(null)

  React.useEffect(() => {
    if (consentName && consentType) {
      getConsentText({
        consentName: consentName,
        consentType: consentType,
      }).then(data => {
        if (data) setConsentObj(data)
      })
    }
  }, [])

  const variantPath = element => `elements.form.variants.${variant}.${element}`
  const ref = validation ? register(validation) : register
  const props = { name, placeholder, errors, setValue, options, uppercase, inputSize }
  const getLinks = {
    termsAndConditions: links?.termsAndConditions,
    privacyPolicy: links?.privacyPolicy,
  }

  const textField =
    (type === 'text' || type === 'redirect') &&
    text.replace(/\$\{([a-z]+)\}/gi, (original, match) => {
      return match in getLinks ? getLinks[match] : original
    })

  // We also support named validation functions
  if (typeof customFunction === 'object') {
    const validateFunctions = {}
    let hasValidationFunctions = false
    for (const key in customFunction) {
      if (customFunctions[key]) {
        // For each custom function used in the data, generate the corresponding validation function

        validateFunctions[key] = customFunctions[key]({ ...customFunction[key], getValues })
        hasValidationFunctions = true
      }
    }
    if (hasValidationFunctions) {
      // See Validate option in https://react-hook-form.com/v5/api/#register

      validation.validate = validateFunctions
    }
  }

  if (customMask && masks[customMask] && customMask !== 'currency' && customMask !== 'numeric') {
    mask = masks[customMask]
  }

  const onChange = e => {
    const { value } = e.target

    if (customMask === 'currency') {
      const currencyString = FormatStringToLocale(value)
      return setValue(name, currencyString)
    }

    if (customMask === 'numeric') {
      const currentValue = isValidNumber(value) ? value : value.slice(0, -1)
      return setValue(name, currentValue)
    }

    setValue(name, value)
  }

  const onKeyDown = e => {
    if (e.keyCode === 13) {
      if (onKeyDownFunction.length) {
        e.preventDefault()
        const func = onKeyDownFunction[0]
        return func()
      }
    }
  }

  const handleTrackInteraction = e => {
    // removed for now due to bug
  }

  const displayLabel = label => {
    return (
      <label
        sx={{
          variant:
            formType === 'calculator'
              ? variantPath('label.variants.calculator')
              : variantPath('label'),
        }}
        htmlFor={label}
      >
        {label}
      </label>
    )
  }

  const displayDescription = description => {
    const journeyVariant = variant === 'journey'
    const sectionNameVariant = sectionTitle && journeyVariant && index !== 0

    return (
      <p
        sx={{
          variant: sectionNameVariant
            ? variantPath('description.sectionName')
            : variantPath('description'),
        }}
      >
        {description}
      </p>
    )
  }

  if (errors[name]) {
    trackComponent('Form Event', 'error', { [name]: errors[name].message })
    trackInteraction(
      'GA Validation Error Triggered',
      'Site Interaction',
      `${name}:${errors[name].message}`,
    )
  }

  return (
    <React.Fragment>
      <If condition={type === 'slider'}>
        <div sx={SLIDER_STYLES}>
          <h2>{label}</h2>
          <input
            type='range'
            min='1'
            max='5'
            step='1'
            onChange={e => console.log(e.target.value)}
            list='tickmarks'
          />

          <datalist
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              color: 'black',
            }}
            id='tickmarks'
          >
            <option value='1' label='Not at all'></option>
            <option value='3' label='Somewhat'></option>
            <option value='5' label='Very'></option>
          </datalist>
        </div>
      </If>
      <If condition={type === 'address-lookup'}>
        <div sx={{ variant: variantPath('input.wrapper') }}>
          {label && displayLabel(label)}
          <AddressHistory
            minimumRequiredYears={minimumRequiredYears}
            name={name}
            lookup={addressLookup}
            label={title}
            onChange={() => {
              // Only do on change validation if we have outstading errors to resolve
              if (errors[name]) {
                triggerValidation(name)
              }
            }}
            ref={ref}
            error={errors[name]}
            formVariant={variant}
          />

          <If condition={subtext}>
            <p sx={{ textAlign: 'left' }}>{subtext}</p>
          </If>
        </div>
      </If>

      <If condition={type === 'address-input'}>
        {label && displayLabel(label)}
        <AddressInput
          name={name}
          lookup={addressLookup}
          label={title}
          onChange={() => {
            if (errors[name]) {
              triggerValidation(name)
            }
          }}
          ref={ref}
          error={errors[name]}
          formVariant={variant}
        />

        <If condition={subtext}>
          <p sx={{ textAlign: 'left' }}>{subtext}</p>
        </If>
      </If>

      <If condition={type === 'hidden'}>
        <input type='hidden' name={name} value={value} ref={ref} />
      </If>

      <If condition={type === 'querystring'}>
        <input type='hidden' name={name} ref={ref} placeholder={placeholder} />
      </If>

      <If condition={type === 'dropdown'}>
        <div
          sx={{
            variant:
              formType === 'calculator'
                ? variantPath('input.calculator.dropdown')
                : isSingleRow
                ? variantPath('input.singleRow')
                : variantPath('input.wrapper'),
          }}
        >
          {label && displayLabel(label)}
          {description && displayDescription(description)}
          <div
            sx={{
              variant:
                formType === 'calculator'
                  ? variantPath('wrapper.variants.calculator')
                  : variantPath('wrapper.base'),
            }}
          >
            <SimpleDropDown
              name={name}
              options={options}
              placeholder={placeholder}
              hasError={errors[name]}
              ref={ref}
              handleTrackInteraction={handleTrackInteraction}
              isSingleRow={isSingleRow}
            />
          </div>
          <If condition={errors[name]}>
            <InputAlert type='alert' sx={{ variant: variantPath('input.error') }}>
              {errors[name].message}
            </InputAlert>
          </If>
          {subtext && <p sx={{ textAlign: 'left' }}>{subtext}</p>}
        </div>
      </If>

      <If condition={type === 'wrapper'}>
        <div
          sx={{
            variant:
              formType === 'calculator'
                ? variantPath('input.wrapper.calculator')
                : variantPath('input.wrapper'),
          }}
        >
          {label && displayLabel(label)}
          <div
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              flexDirection: [isSingleRow ? 'row' : 'column', 'row'],
            }}
          >
            <For each='child' of={children} index='key'>
              <div
                sx={{
                  width: [isSingleRow ? '30%' : '100%', '30%'],
                  marginTop: [isSingleRow ? 0 : 'xs', 0],
                  '&:first-of-type': { marginTop: 0 },
                }}
                key={key}
              >
                <FormStepField
                  {...{
                    field: { ...child, fieldVariant },
                    register,
                    errors,
                    setValue,
                    getValues,
                    formType,
                    variant,
                    goToPage,
                    updateCompletionState,
                    onKeyDownFunction,
                    isChild: true,
                  }}
                />
              </div>
            </For>
          </div>
        </div>
      </If>

      <If condition={type === 'radio'}>
        <div sx={{ variant: variantPath('input.radio.wrapper') }}>
          {label && displayLabel(label)}
          {description && displayDescription(description)}
          <div
            sx={{
              variant:
                formType === 'calculator'
                  ? variantPath('simple-radio.radioWrapper.variants.calculator')
                  : variantPath('simple-radio.radioWrapper'),
            }}
          >
            {options.map(({ label, value, defaultChecked = false, toolTip }, i) => (
              <div
                sx={{
                  variant:
                    formType === 'calculator'
                      ? variantPath('simple-radio.span.variants.calculator')
                      : variantPath('simple-radio.span'),
                }}
                key={i}
              >
                <SimpleRadio
                  key={i}
                  defaultChecked={defaultChecked}
                  label={label}
                  name={name}
                  value={value}
                  ref={ref}
                  onClick={handleTrackInteraction}
                  toolTip={toolTip}
                />
              </div>
            ))}
          </div>
          <If condition={errors[name]}>
            <InputAlert
              type='alert'
              sx={{ variant: variantPath('input.error'), position: 'relative', top: 0 }}
            >
              {errors[name].message}
            </InputAlert>
          </If>
        </div>
      </If>

      <If condition={type === 'radio-horizontal'}>
        <div sx={{ variant: variantPath('input.radio.wrapper') }}>
          {label && displayLabel(label)}
          {description && displayDescription(description)}
          <div
            sx={{
              variant:
                formType === 'calculator'
                  ? variantPath('simple-radio.radioWrapper.variants.calculator')
                  : variantPath('simple-radio.radioWrapper'),
              flexDirection: 'row',
              justifyContent: 'space-between',
            }}
          >
            {options.map(({ label, value, defaultChecked = false }, i) => (
              <div
                sx={{
                  variant:
                    formType === 'calculator'
                      ? variantPath('simple-radio.span.variants.calculator')
                      : variantPath('simple-radio.span'),
                  width: 'calc(50% - 8px)',
                }}
                key={i}
              >
                <SimpleRadio
                  key={i}
                  defaultChecked={defaultChecked}
                  label={label}
                  name={name}
                  value={value}
                  ref={ref}
                  onClick={handleTrackInteraction}
                  hasError={errors[name]}
                />
              </div>
            ))}
          </div>
          <If condition={errors[name]}>
            <InputAlert
              type='alert'
              sx={{ variant: variantPath('input.error'), position: 'relative', top: 0 }}
            >
              {errors[name].message}
            </InputAlert>
          </If>
        </div>
      </If>

      <If condition={type === 'checkbox'}>
        <div sx={{ variant: variantPath('input.checkbox.wrapper') }}>
          {label && displayLabel(label)}
          {description && displayDescription(description)}
          <div
            sx={{
              variant:
                formType === 'calculator'
                  ? variantPath('checkbox.wrapper.variants.calculator')
                  : variantPath('checkbox.wrapper'),
            }}
          >
            {options.map(({ label, value, defaultChecked = false }, i) => (
              <div
                sx={{
                  variant:
                    formType === 'calculator'
                      ? variantPath('checkbox.span.variants.calculator')
                      : variantPath('checkbox.span'),
                }}
                key={i}
              >
                <SimpleCheckbox
                  key={i}
                  defaultChecked={defaultChecked}
                  label={label}
                  value={value}
                  name={name}
                  ref={ref}
                  onBlur={handleTrackInteraction}
                />
              </div>
            ))}
          </div>

          <If condition={errors[name]}>
            <InputAlert type='alert' sx={{ variant: variantPath('input.error') }}>
              {errors[name].message}
            </InputAlert>
          </If>
        </div>
      </If>

      <If condition={type === 'autoComplete'}>
        <div sx={{ variant: variantPath('input.wrapper') }}>
          {label && displayLabel(label)}
          {description && displayDescription(description)}
          <SuggestionBox
            ref={ref}
            name={name}
            placeholder={placeholder}
            suggestionData={suggestionData}
          />
          <If condition={errors[name]}>
            <InputAlert
              type='alert'
              sx={{ variant: variantPath('input.error'), position: 'relative', top: 'sm' }}
            >
              {errors[name].message}
            </InputAlert>
          </If>
        </div>
      </If>

      <If condition={type === 'summary'}>
        <div sx={{ variant: variantPath('input.wrapper') }}>
          <h2>{title}</h2>
          <ul>
            {Object.entries(summaryList).map(([key, value], i) => (
              <li sx={{ display: 'flex' }} key={i}>
                {key}: {formatter(getValues(value.value), value.type)}
                <button
                  type='button'
                  sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                  onClick={() => {
                    updateCompletionState(i, false)
                    goToPage(i)
                  }}
                >
                  Edit
                </button>
              </li>
            ))}
          </ul>

          <If condition={errors[name]}>
            <InputAlert type='alert' sx={{ variant: variantPath('input.error') }}>
              {errors[name].message}
            </InputAlert>
          </If>
        </div>
      </If>

      <If condition={type === 'input' || type === 'tel'}>
        <div
          sx={{
            variant:
              formType === 'calculator'
                ? variantPath('input.wrapper.calculator')
                : isSingleRow
                ? variantPath('input.singleRow')
                : variantPath('input.wrapper'),
          }}
        >
          {label && displayLabel(label)}
          {description && displayDescription(description)}
          <div
            sx={{
              variant:
                formType === 'calculator'
                  ? variantPath('wrapper.variants.calculator')
                  : variantPath('wrapper.base'),
            }}
          >
            <Choose>
              <When condition={mask}>
                <InputMask
                  name={name}
                  type={type}
                  maskChar={0}
                  onChange={onChange}
                  mask={mask}
                  onKeyDown={onKeyDown}
                >
                  {inputProps => (
                    <SimpleInput
                      prefix={prefix}
                      suffix={suffix}
                      ref={ref}
                      {...inputProps}
                      hasError={errors[name]}
                      name={name}
                      placeholder={props.placeholder}
                      onChange={onChange}
                      onKeyDown={onKeyDown}
                      onBlur={handleTrackInteraction}
                    />
                  )}
                </InputMask>
              </When>
              <Otherwise>
                <SimpleInput
                  prefix={prefix}
                  suffix={suffix}
                  ref={ref}
                  hasError={errors[name]}
                  onChange={onChange}
                  name={name}
                  type={type}
                  placeholder={props.placeholder}
                  onKeyDown={onKeyDown}
                  onBlur={handleTrackInteraction}
                />
              </Otherwise>
            </Choose>

            {subtext && <p sx={{ textAlign: 'left' }}>{subtext}</p>}
            {consentObj && (
              <React.Fragment>
                {fieldVariant === 'consent' && (
                  <p sx={{ fontWeight: 'bold', mt: 'xl' }}>Keep in touch with us?</p>
                )}
                <div
                  sx={{
                    variant: 'elements.input.consent.wrapper',
                    marginTop: 'sm',
                    textAlign: 'left',
                  }}
                >
                  {fieldVariant === 'consentMoney' && (
                    <p sx={{ variant: 'elements.input.consent.question' }}>
                      Want to stay in touch?
                    </p>
                  )}
                  <Checkbox
                    defaultChecked={false}
                    label={consentObj.text}
                    name={`${name}-checkbox`}
                    ref={register}
                    onBlur={handleTrackInteraction}
                    sx={{
                      variant: 'elements.input.checkbox.variants.consent',
                    }}
                  >
                    <div>
                      <p sx={{ variant: 'elements.input.consent.text', my: '0' }}>
                        {consentObj.text}
                      </p>
                      {fieldVariant === 'consent' && (
                        <small>
                          You can unsubscribe at any time.{' '}
                          <Link href={privacyPolicyLink?.href || '/'} sx={{ color: 'grey-80' }}>
                            {privacyPolicyLink?.name || 'Privacy policy'}
                          </Link>
                        </small>
                      )}
                    </div>
                  </Checkbox>
                </div>
              </React.Fragment>
            )}
            <If condition={errors[name]}>
              <InputAlert
                type='alert'
                sx={{ pl: '0', pr: '0', variant: variantPath('input.error') }}
              >
                {errors[name].message}
              </InputAlert>
            </If>
          </div>
        </div>
      </If>

      <If condition={type === 'text' || type === 'redirect'}>
        <div sx={{ variant: variantPath('input.wrapper') }}>
          <label sx={{ variant: variantPath('label') }}>{label}</label>
          <div
            sx={{
              pointerEvents: 'auto',
              variant: type === 'redirect' ? variantPath('redirect-text') : '',
            }}
            dangerouslySetInnerHTML={{ __html: textField }}
          ></div>
        </div>
      </If>
    </React.Fragment>
  )
}

export default FormStepField
